import React, { useState, useEffect } from 'react';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import TranslateIcon from '@mui/icons-material/Translate';
import LanguageIcon from '@mui/icons-material/Language';
import DeleteIcon from '@mui/icons-material/Delete';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { setSelectedLanguage } from '@modules/subtitle';

import AddSubtitleModal from './AddSubtitleModal';
import TranslateSubtitleModal from './TranslateSubtitleModal';
import SubLangStateSwitch from './SubLangStateSwitch';
import DeleteSubtitleModal from './DeleteSubtitleModal';

const LanguageSelectorPanel = ({
    isAddModalOpen,
    setIsAddModalOpen,
    currentSelectedLanguage,
    setCurrentSelectedLanguage,
    availableSubtitleLanguages,
    setAvailableSubtitleLanguages,
    isLoading = false,
    setShowNoSub
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);
    const [subtitleLanguageStates, setSubtitleLanguageStates] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [languageToDelete, setLanguageToDelete] = useState(null);

    // 사용 가능한 자막 언어 목록이 변경될 때마다 언어 상태 초기화
    useEffect(() => {
        if (availableSubtitleLanguages && availableSubtitleLanguages.length > 0) {
            console.log("availableSubtitleLanguages", availableSubtitleLanguages, currentSelectedLanguage);
            const initialStates = {};
            availableSubtitleLanguages.forEach(lang => {
                initialStates[lang.lang] = lang.state === 1;
            });
            setSubtitleLanguageStates(initialStates);

            // 현재 선택된 언어가 있다면, 갱신된 목록에서 동일한 코드를 가진 언어로 업데이트
            if (currentSelectedLanguage) {
                const updatedCurrentLang = availableSubtitleLanguages.find(
                    lang => lang.lang === currentSelectedLanguage.lang
                );
                if (updatedCurrentLang) {
                    setCurrentSelectedLanguage(updatedCurrentLang);
                }
            }
        }
        else if (availableSubtitleLanguages.length === 0 && currentSelectedLanguage) {
            // 빈 배열일 때만 currentSelectedLanguage를 초기화
            setCurrentSelectedLanguage(null);
            const pathSegments = location.pathname.split('/');
            const subtitleIndex = pathSegments.findIndex(segment => segment === 'subtitle');

            if (subtitleIndex !== -1) {
                const newPath = pathSegments.slice(0, subtitleIndex + 1).join('/');
                navigate(newPath, { replace: true });
            }
        }
    }, [availableSubtitleLanguages]); // currentSelectedLanguage 의존성 제거

    // 언어 선택 시 현재 선택된 언어 업데이트
    const handleLanguageChange = (lang) => {
        const selectedLangObject = availableSubtitleLanguages.find(subLang => subLang.lang === lang);

        if (selectedLangObject) {
            setCurrentSelectedLanguage(selectedLangObject);
            dispatch(setSelectedLanguage(selectedLangObject));
            const pathSegments = location.pathname.split('/');
            const subtitleIndex = pathSegments.findIndex(segment => segment === 'subtitle');

            if (subtitleIndex !== -1) {
                const newPath = pathSegments.slice(0, subtitleIndex + 1).join('/') + '/' + lang;
                console.log("newPath", newPath);
                navigate(newPath, { replace: true });
            }
        }
    };

    const handleDeleteClick = (lang) => {
        setLanguageToDelete(lang);
        setDeleteDialogOpen(true);
    };


    return (
        <>
            <div className='box-language-selector language-selector-panel'>
                <div className='box-subEdit-btns'>
                    {isLoading ? (
                        <>
                            <Skeleton variant="rectangular" width={180} height={36} className="skeleton-loading skeleton-btn" />
                            <Skeleton variant="rectangular" width={180} height={36} className="skeleton-loading skeleton-btn" />
                            <Skeleton variant="rectangular" width={200} height={36} className="skeleton-loading skeleton-btn" />
                        </>
                    ) : (
                        <>
                            <Button
                                className="btn-sub-edit"
                                variant="contained"
                                startIcon={<AddIcon />}
                                color="primary"
                                onClick={() => setIsAddModalOpen(true)}
                            >
                                {t('vod.manage.subtitle_tab.create_subtitle')}
                            </Button>

                            <Button
                                className="btn-sub-edit"
                                variant="contained"
                                startIcon={<TranslateIcon />}
                                color="success"
                                onClick={() => setIsTranslateModalOpen(true)}
                            >
                                {t('vod.manage.subtitle_tab.translate_subtitle')}
                            </Button>

                            <Button
                                className="btn-sub-set-state btn-set-state"
                                variant="outlined"
                                onClick={(e) => { setAnchorEl(e.currentTarget); setIsPopoverOpen(!isPopoverOpen) }}
                            >
                                <Box className="btn-set-state-content">
                                    <LanguageIcon className="btn-set-state-icon" />
                                    <Typography className="btn-set-state-text">
                                        {t('vod.manage.usage_setting')}
                                    </Typography>
                                </Box>
                                <Box className="btn-set-state-actions">
                                    <Chip
                                        label={`${t('settings.general.language')} ${currentSelectedLanguage?.state ? t('vod.manage.use') : t('vod.manage.un_use')}`}
                                        size="small"
                                        className={`btn-set-state-chip ${currentSelectedLanguage?.state ? 'chip-active' : 'chip-inactive'}`}
                                    />
                                    <KeyboardArrowDown className="btn-set-state-arrow" />
                                </Box>
                            </Button>

                            <Popover
                                open={isPopoverOpen}
                                anchorEl={anchorEl}
                                onClose={() => setIsPopoverOpen(false)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                disablePortal
                            >
                                {
                                    currentSelectedLanguage ? (
                                        <Box className="popover-language-settings">
                                            <div className="popover-content">
                                                <Typography variant="h6" gutterBottom className="popover-title">
                                                    {t('vod.manage.current_language_settings')}
                                                </Typography>
                                                <div className="popover-current-language">
                                                    <div className="current-language-info">
                                                        <LanguageIcon className="current-language-icon" />
                                                        <span className="current-language-name">{t(`languages.${currentSelectedLanguage.lang}`)}</span>
                                                    </div>
                                                    <div className="current-language-actions">
                                                        <SubLangStateSwitch
                                                            language={currentSelectedLanguage}
                                                            setAvailableSubtitleLanguages={setAvailableSubtitleLanguages}
                                                            setSubtitleLanguageStates={setSubtitleLanguageStates}
                                                            setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteClick(currentSelectedLanguage)}
                                                            className="btn-delete-language"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </div>
                                                </div>

                                                {availableSubtitleLanguages.length > 1 && (
                                                    <>
                                                        <Typography variant="h6" className="popover-other-languages">
                                                            <div className="other-languages-title">
                                                                {t('vod.manage.other_language_settings')}
                                                            </div>
                                                            {availableSubtitleLanguages
                                                                .filter(lang => lang.lang !== currentSelectedLanguage.lang)
                                                                .map((lang, idx) => (
                                                                    <div
                                                                        key={idx}
                                                                        className="other-language-item"
                                                                    >
                                                                        <Typography className="other-language-name">{t(`languages.${lang.lang}`)}</Typography>

                                                                        <div className="other-language-actions">
                                                                            <Chip
                                                                                label={subtitleLanguageStates[lang.lang] ? "ON" : "OFF"}
                                                                                size="small"
                                                                                className={`other-language-chip ${subtitleLanguageStates[lang.lang] ? 'chip-active' : 'chip-inactive'}`}
                                                                            />

                                                                            <SubLangStateSwitch
                                                                                language={lang}
                                                                                setAvailableSubtitleLanguages={setAvailableSubtitleLanguages}
                                                                                setSubtitleLanguageStates={setSubtitleLanguageStates}
                                                                                setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                                                                            />

                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleDeleteClick(lang)}
                                                                                className="btn-delete-other-language"
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </Typography>
                                                    </>
                                                )}
                                            </div>
                                        </Box>
                                    ) : (
                                        <Box className="popover-no-settings">
                                            <Typography variant="body1">
                                                {t('vod.manage.subtitle_tab.no_subtitle_settings')}
                                            </Typography>
                                        </Box>
                                    )
                                }
                            </Popover>
                        </>
                    )}
                </div>

                {
                    currentSelectedLanguage && (
                        <div className="language-selector">
                            {isLoading ? (
                                <Skeleton variant="rectangular" width="100%" height={56} className="skeleton-loading skeleton-form" />
                            ) : (
                                <FormControl className="language-selector-form" variant="outlined" fullWidth>
                                    <InputLabel id="subtitle-language-label">
                                        {t('vod.manage.subtitle_tab.subtitle_language')}
                                    </InputLabel>
                                    <Select
                                        labelId="subtitle-language-label"
                                        id="subtitle-language-select"
                                        value={currentSelectedLanguage.lang}
                                        onChange={(event) => handleLanguageChange(event.target.value)}
                                        // label={t('vod.manage.subtitle_tab.subtitle_language')}
                                        label={currentSelectedLanguage.lang}
                                    >
                                        {availableSubtitleLanguages.map((el, idx) => (
                                            <MenuItem key={idx} value={el.lang} className="menu-item-language">
                                                {t(`languages.${el.lang}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </div>
                    )
                }
            </div>

            <AddSubtitleModal
                availableSubtitleLanguages={availableSubtitleLanguages}
                setAvailableSubtitleLanguages={setAvailableSubtitleLanguages}
                setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                isAddModalOpen={isAddModalOpen}
                setIsAddModalOpen={setIsAddModalOpen}
                setShowNoSub={setShowNoSub}
            />

            <TranslateSubtitleModal
                isTranslateModalOpen={isTranslateModalOpen}
                setIsTranslateModalOpen={setIsTranslateModalOpen}
                availableSubtitleLanguages={availableSubtitleLanguages}
                setAvailableSubtitleLanguages={setAvailableSubtitleLanguages}
            />

            <DeleteSubtitleModal
                setDeleteDialogOpen={setDeleteDialogOpen}
                deleteDialogOpen={deleteDialogOpen}
                languageToDelete={languageToDelete}
                setLanguageToDelete={setLanguageToDelete}
                availableSubtitleLanguages={availableSubtitleLanguages}
                setAvailableSubtitleLanguages={setAvailableSubtitleLanguages}
                currentSelectedLanguage={currentSelectedLanguage}
                setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                setShowNoSub={setShowNoSub}
            />
        </>
    );
};

export default LanguageSelectorPanel;